import { cn, getTextColor } from "@/lib/utils";
import { forwardRef } from "react";
import { Tag } from "@/types/v2/Tags";
import { EditTagDialog } from "@/components/Tags/EditTagDialog";
import { DEFAULT_TAG_COLOR } from "@/lib/const";
import { Icon } from "@/components/Common/Icon";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export interface TagItemProps {
  tag: Tag;
  onEdit?: (tag: Tag) => Promise<void>; // Callback for the edit button
  onDelete?: (tag: Tag) => Promise<void>; // Callback for the delete button
}

export interface TagListProps {
  tags: Tag[]; // List of tags with their props
  className?: string; // Optional additional classes
  onEdit?: (tag: Tag) => Promise<void>; // Callback for the edit button
  onDelete?: (tag: Tag) => Promise<void>; // Callback for the delete button
  /** Called with the full tag id list in its new order after a drag. Omit to render a non-draggable list. */
  onReorder?: (orderedIds: string[]) => void;
}

const TagItem = forwardRef<HTMLDivElement, TagItemProps>(
  ({ tag, onEdit, onDelete }, ref) => (
    <div
      ref={ref}
      className="flex items-center justify-between rounded-md px-4 py-1"
    >
      <div className="flex items-center space-x-4">
        <div
          className="flex h-[40px] w-[150px] items-center justify-center rounded-sm border"
          style={{
            backgroundColor: tag.customColor || DEFAULT_TAG_COLOR,
            color: getTextColor(tag.customColor || DEFAULT_TAG_COLOR),
          }}
        >
          {tag.label && (
            <span className={cn("text-sm font-medium")}>{tag.label}</span>
          )}
        </div>
      </div>
      <EditTagDialog
        tag={tag}
        onSave={(updatedTag) => {
          if (onEdit) {
            onEdit(updatedTag);
          }
        }}
        onDelete={() => {
          if (onDelete) {
            onDelete(tag);
          }
        }}
      />
    </div>
  )
);
TagItem.displayName = "TagItem";

const SortableTagItem = ({ tag, onEdit, onDelete }: TagItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tag.id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className={cn(
        "flex items-center gap-2 rounded-md border bg-background",
        isDragging ? "border-primary" : "border-transparent"
      )}
    >
      <div
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label="Reorder tag"
        className="cursor-grab pl-2 text-muted-foreground hover:text-foreground active:cursor-grabbing"
      >
        <Icon name="gripVertical" className="h-4 w-4" />
      </div>
      <div className="flex-1">
        <TagItem tag={tag} onEdit={onEdit} onDelete={onDelete} />
      </div>
    </div>
  );
};

const TagList = forwardRef<HTMLDivElement, TagListProps>(
  ({ tags, className, onEdit, onDelete, onReorder }, ref) => {
    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    const handleDragEnd = ({ active, over }: DragEndEvent) => {
      if (!onReorder || !over || active.id === over.id) return;

      const ids = tags.map((tag) => tag.id);
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;

      onReorder(arrayMove(ids, oldIndex, newIndex));
    };

    if (tags.length === 0) {
      return (
        <div ref={ref} className={cn("flex flex-col space-y-2", className)}>
          <div className="text-center text-muted-foreground">
            No tags found. Tags will appear here when created.
          </div>
        </div>
      );
    }

    if (!onReorder) {
      return (
        <div ref={ref} className={cn("flex flex-col space-y-2", className)}>
          {tags.map((tag) => (
            <TagItem
              key={tag.id}
              tag={tag}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      );
    }

    return (
      <div ref={ref} className={cn("flex flex-col space-y-2", className)}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={tags.map((tag) => tag.id)}
            strategy={verticalListSortingStrategy}
          >
            {tags.map((tag) => (
              <SortableTagItem
                key={tag.id}
                tag={tag}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    );
  }
);
TagList.displayName = "TagList";

export { TagList, TagItem };
